import { Box, CloseButton, Group, Paper, Text, Transition, useMantineTheme } from '@mantine/core';
import { IconCheck, IconX, IconInfoCircle, IconAlertTriangle } from '@tabler/icons-react';
import { forwardRef, useEffect, useState } from 'react';
import { useReducedMotion } from '../../animations/hooks';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  title: string;
  message?: string;
  type?: ToastType;
  gradient?: boolean;
  gradientVariant?: GradientVariant;
  duration?: number;
  onClose?: () => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

const Toast = forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      title,
      message,
      type = 'info',
      gradient = false,
      gradientVariant = 'primary',
      duration = 5000,
      onClose,
      position = 'top-right',
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const [mounted, setMounted] = useState(true);
    const reducedMotion = useReducedMotion();
    const gradientConfig = theme.other.gradients[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const typeConfig = {
      success: { color: theme.colors.green[6], icon: <IconCheck size={20} /> },
      error: { color: theme.colors.red[6], icon: <IconX size={20} /> },
      warning: { color: theme.colors.yellow[6], icon: <IconAlertTriangle size={20} /> },
      info: { color: theme.colors.blue[6], icon: <IconInfoCircle size={20} /> },
    };

    const config = typeConfig[type];

    useEffect(() => {
      if (duration > 0) {
        const timer = setTimeout(() => {
          setMounted(false);
          setTimeout(() => onClose?.(), reducedMotion ? 0 : 200);
        }, duration);
        return () => clearTimeout(timer);
      }
    }, [duration, onClose, reducedMotion]);

    const handleClose = () => {
      setMounted(false);
      setTimeout(() => onClose?.(), reducedMotion ? 0 : 200);
    };

    const positionStyles = {
      'top-right': { top: 16, right: 16 },
      'top-left': { top: 16, left: 16 },
      'bottom-right': { bottom: 16, right: 16 },
      'bottom-left': { bottom: 16, left: 16 },
    };

    return (
      <Transition mounted={mounted} transition={reducedMotion ? 'fade' : 'slide-left'} duration={reducedMotion ? 0 : 200}>
        {(styles) => (
          <Paper
            ref={ref}
            shadow="md"
            p="md"
            radius="md"
            style={{
              position: 'fixed',
              ...positionStyles[position],
              zIndex: 9999,
              minWidth: 300,
              maxWidth: 400,
              ...(gradient
                ? {
                    backgroundImage: `linear-gradient(135deg, ${gradientConfig.from}, ${gradientConfig.to})`,
                    color: 'white',
                  }
                : {}),
              ...styles,
            }}
          >
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Group gap="sm" wrap="nowrap">
                <Box
                  style={{
                    color: gradient ? 'white' : config.color,
                  }}
                >
                  {config.icon}
                </Box>
                <Box>
                  <Text fw={600} size="sm" style={accessibilityStyles}>
                    {title}
                  </Text>
                  {message && (
                    <Text size="xs" opacity={0.8} mt={4} style={accessibilityStyles}>
                      {message}
                    </Text>
                  )}
                </Box>
              </Group>
              <CloseButton
                size="sm"
                onClick={handleClose}
                style={{ color: gradient ? 'white' : undefined }}
              />
            </Group>
          </Paper>
        )}
      </Transition>
    );
  }
);

Toast.displayName = 'Toast';

export default Toast;
